<script setup lang="ts">
import { watch } from 'vue';
import { useForm } from 'vee-validate';
import { cloneDeep } from 'lodash-es';

import { defaultApplicantIncome, defaultPeriodicValue } from '@/helpers/builder';
import { EmploymentType } from '@/helpers/const';
import { simpleSalarySchema } from '@/validation';
import { forceScrollToTop } from '@/helpers/forceScrollToTop';

import type { ApplicantIncome, SalaryEmployment } from '@/types';

const props = defineProps<{
  applicantId: string;
  modelValue: ApplicantIncome | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: ApplicantIncome): void;
}>();

const { meta, submitCount, handleSubmit, resetForm } = useForm<SalaryEmployment>({
  validationSchema: simpleSalarySchema,
  validateOnMount: false,
});

watch(
  () => props.modelValue,
  (newVal) => {
    const employment = newVal?.employment?.[0];
    if (employment && 'salary' in employment) {
      resetForm({ values: employment as SalaryEmployment });
    } else {
      resetForm({
        values: {
          type: EmploymentType.FULL_TIME,
          salary: defaultPeriodicValue(),
        },
      });
    }
  },
  { immediate: true, deep: true },
);

const update = handleSubmit(
  (formValues) => {
    const updatedIncome = cloneDeep(props.modelValue ?? defaultApplicantIncome());

    if (!updatedIncome.employment || updatedIncome.employment.length === 0) {
      updatedIncome.employment = [{} as SalaryEmployment];
    }

    Object.assign(updatedIncome.employment[0], { ...formValues, type: EmploymentType.FULL_TIME });

    emit('update:modelValue', updatedIncome);
    forceScrollToTop(0);
    return true;
  },
  () => {
    forceScrollToTop(0);
    return false;
  },
);

defineExpose({ update });
</script>

<template>
  <div>
    <BxField v-slot="{ field }" name="salary">
      <BxPeriodicAmount
        :id="applicantId + '-salary'"
        v-bind="field"
        label="Income amount"
        label-type="floating"
        leading-symbol="$"
        class="form-control"
      />
    </BxField>
    <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
      Please review the error messages above.
    </BxErrorPanel>
  </div>
</template>
